export const SCROLL_LOCK_ALLOW_ATTR = 'data-scroll-lock-allow';

/** Headless UI portal wrappers for anchored ListboxOptions / PopoverPanel / MenuItems. */
export const HEADLESS_UI_PORTAL_ATTR = 'data-headlessui-portal';

const SCROLL_LOCK_ALLOW_ROOT_SELECTOR = `[${SCROLL_LOCK_ALLOW_ATTR}], [${HEADLESS_UI_PORTAL_ATTR}]`;

export const findScrollLockAllowRoot = (target: Element): Element | null =>
  target.closest(SCROLL_LOCK_ALLOW_ROOT_SELECTOR);

export const isScrollableOverflowY = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = getComputedStyle(element);
  const overflowY = style.overflowY;
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
    return false;
  }

  return element.scrollHeight > element.clientHeight;
};

export const isScrollableOverflowX = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = getComputedStyle(element);
  const overflowX = style.overflowX;
  if (overflowX !== 'auto' && overflowX !== 'scroll' && overflowX !== 'overlay') {
    return false;
  }

  return element.scrollWidth > element.clientWidth;
};

export const canElementScrollInDirection = (element: Element, deltaY: number): boolean => {
  if (!(element instanceof HTMLElement) || deltaY === 0 || !isScrollableOverflowY(element)) {
    return false;
  }

  const { scrollTop, scrollHeight, clientHeight } = element;
  if (deltaY < 0) {
    return scrollTop > 0;
  }

  return scrollTop + clientHeight < scrollHeight - 1;
};

export const canElementScrollInDirectionY = canElementScrollInDirection;

export const canElementScrollInDirectionX = (element: Element, deltaX: number): boolean => {
  if (!(element instanceof HTMLElement) || deltaX === 0 || !isScrollableOverflowX(element)) {
    return false;
  }

  const { scrollLeft, scrollWidth, clientWidth } = element;
  if (deltaX < 0) {
    return scrollLeft > 0;
  }

  return scrollLeft + clientWidth < scrollWidth - 1;
};

const hasScrollableAncestorWithin = (target: Element, allowRoot: Element): boolean => {
  let current: Element | null = target;
  while (current && allowRoot.contains(current)) {
    if (isScrollableOverflowY(current) || isScrollableOverflowX(current)) {
      return true;
    }
    if (current === allowRoot) {
      break;
    }
    current = current.parentElement;
  }

  return false;
};

/**
 * Wheel events are allowed only while a scrollable ancestor inside a
 * `[data-scroll-lock-allow]` region (or a portaled Headless UI surface) can still
 * move in that direction. At boundaries (or outside allow regions) return false so
 * callers can preventDefault and stop nested page scrollers from moving behind a modal.
 */
export const shouldAllowWheelEvent = (
  target: EventTarget | null,
  deltaY: number,
  deltaX = 0,
): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  const allowRoot = findScrollLockAllowRoot(target);
  if (!allowRoot) {
    return false;
  }

  let current: Element | null = target;
  while (current && allowRoot.contains(current)) {
    const canScrollY = deltaY !== 0 && canElementScrollInDirection(current, deltaY);
    const canScrollX = deltaX !== 0 && canElementScrollInDirectionX(current, deltaX);
    if (canScrollY || canScrollX) {
      return true;
    }
    if (current === allowRoot) {
      break;
    }
    current = current.parentElement;
  }

  return false;
};

/**
 * Touch move is allowed inside explicit `[data-scroll-lock-allow]` modal surfaces.
 * Portaled Headless UI lists/panels only unblock when they contain a scrollable
 * overflow ancestor — otherwise preventDefault keeps the page locked.
 */
export const shouldAllowTouchMoveEvent = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  const allowRoot = findScrollLockAllowRoot(target);
  if (!allowRoot) {
    return false;
  }

  if (allowRoot.hasAttribute(SCROLL_LOCK_ALLOW_ATTR)) {
    return true;
  }

  return hasScrollableAncestorWithin(target, allowRoot);
};
